import React, { useRef, useState } from 'react';
import {
  Box,
  Button,
  ClickAwayListener,
  IconButton,
  InputAdornment,
  Popper,
  Stack,
  TextField,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from '@mui/material';
import SearchIcon from '@mui/icons-material/Search';
import ViewListIcon from '@mui/icons-material/ViewList';
import GridViewIcon from '@mui/icons-material/GridView';
import TuneIcon from '@mui/icons-material/Tune';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import CheckIcon from '@mui/icons-material/Check';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import CloseIcon from '@mui/icons-material/Close';
import EditIcon from '@mui/icons-material/Edit';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import LabelOutlinedIcon from '@mui/icons-material/LabelOutlined';
import {
  STATUS_FILTER_CHIPS,
  StatusFilterValue,
  VOCAB_STATUSES,
  WordStatus,
  getLevelColor,
  hexToRgba,
  statusColor,
} from './meta';
import {
  GroupBy,
  UseVocabularyVerbsViewState,
  ViewMode,
} from './useVocabularyVerbsView';

interface FilterBarProps {
  view: UseVocabularyVerbsViewState;
  /** Levels present in the dataset — used to seed the level dropdown. */
  levelOptions: string[];
  /** Sets the lifecycle status on every selected verb. */
  onBulkSetStatus: (status: WordStatus) => void;
  onBulkDelete: () => void;
  /** Anchor for the parent-owned settings dialog/popover. */
  settingsAnchorRef: React.MutableRefObject<HTMLButtonElement | null>;
  onToggleSettings: () => void;
  settingsOpen?: boolean;
}

const STATUS_ORDER_BUTTONS: WordStatus[] = ['ACTIVE', 'LEARNED', 'REPEAT', 'NEW'];

// Legacy individual-chip helper (one-pill-per-status) — replaced by the
// single segmented StatusChips container below. Kept here as a no-op
// component import to avoid breaking any external references; the inline
// rendering in StatusChips is what the UI uses.
const _ChipButton: React.FC<{
  active: boolean;
  onClick: () => void;
  dotColor?: string;
  children: React.ReactNode;
}> = ({ active, onClick, dotColor, children }) => (
  <Box
    component={'button'}
    onClick={onClick}
    sx={{
      alignItems: 'center',
      bgcolor: active ? 'primary.main' : 'background.paper',
      border: '1px solid',
      borderColor: active ? 'primary.main' : 'primary.tableBorder',
      borderRadius: 999,
      color: active ? 'common.white' : 'text.secondary',
      cursor: 'pointer',
      display: 'inline-flex',
      flex: 'none',
      fontFamily: 'inherit',
      fontSize: 13,
      fontWeight: 700,
      gap: 0.75,
      height: 34,
      px: 1.5,
      transition: 'all .14s ease',
      whiteSpace: 'nowrap',
      '&:hover': { borderColor: 'primary.main' },
    }}
  >
    {dotColor && (
      <Box
        sx={{
          bgcolor: active ? 'common.white' : dotColor,
          borderRadius: '50%',
          flex: 'none',
          height: 8,
          width: 8,
        }}
      />
    )}
    {children}
  </Box>
);

const SegButton: React.FC<{
  active: boolean;
  icon?: React.ReactNode;
  title: string;
  showLabel?: boolean;
  onClick: () => void;
}> = ({ active, icon, title, showLabel = true, onClick }) => (
  <Box
    component={'button'}
    onClick={onClick}
    title={title}
    sx={{
      alignItems: 'center',
      bgcolor: active ? 'primary.main' : 'transparent',
      border: 'none',
      borderRadius: 999,
      color: active ? 'common.white' : 'text.secondary',
      cursor: 'pointer',
      display: 'inline-flex',
      fontFamily: 'inherit',
      fontSize: 13,
      fontWeight: 700,
      gap: 0.625,
      height: 32,
      px: 1.625,
      transition: 'all .14s ease',
    }}
  >
    {icon}
    {showLabel && title}
  </Box>
);

const SearchInput: React.FC<{ view: UseVocabularyVerbsViewState }> = ({ view }) => (
  <TextField
    size={'small'}
    value={view.search}
    onChange={(e) => view.setSearch(e.target.value)}
    placeholder={'Пошук: go, went, йти…'}
    InputProps={{
      startAdornment: (
        <InputAdornment position={'start'}>
          <SearchIcon sx={{ color: 'text.secondary', fontSize: 19 }} />
        </InputAdornment>
      ),
      sx: {
        bgcolor: 'background.paper',
        borderRadius: 999,
        fontSize: 14,
        height: 40,
        '& fieldset': { borderRadius: 999 },
      },
    }}
    sx={{ flex: '1 1 240px', maxWidth: { xs: '100%', md: 340 } }}
  />
);

const ViewToggle: React.FC<{
  view: UseVocabularyVerbsViewState;
  showLabels?: boolean;
}> = ({ view, showLabels = true }) => (
  <Box
    sx={{
      bgcolor: 'background.paper',
      border: '1px solid',
      borderColor: 'primary.tableBorder',
      borderRadius: 999,
      display: 'inline-flex',
      flex: 'none',
      gap: '2px',
      p: '3px',
    }}
  >
    <SegButton
      active={view.viewMode === 'table'}
      icon={<ViewListIcon sx={{ fontSize: 18 }} />}
      title={'Таблиця'}
      showLabel={showLabels}
      onClick={() => view.setViewMode('table' as ViewMode)}
    />
    <SegButton
      active={view.viewMode === 'cards'}
      icon={<GridViewIcon sx={{ fontSize: 18 }} />}
      title={'Картки'}
      showLabel={showLabels}
      onClick={() => view.setViewMode('cards' as ViewMode)}
    />
  </Box>
);

const GroupByControl: React.FC<{
  view: UseVocabularyVerbsViewState;
  compact?: boolean;
}> = ({ view, compact }) => {
  const opts: Array<[GroupBy, string]> = [
    ['letter', 'Абетка'],
    ['recent', 'Нещодавні'],
    ['none', 'Без груп'],
  ];
  return (
    <Stack direction={'row'} alignItems={'center'} spacing={1}>
      {!compact && (
        <Typography
          sx={{
            color: 'text.secondary',
            fontSize: 12.5,
            fontWeight: 700,
            whiteSpace: 'nowrap',
          }}
        >
          {'Групувати:'}
        </Typography>
      )}
      <Box
        sx={{
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: 'primary.tableBorder',
          borderRadius: 999,
          display: 'inline-flex',
          flex: 'none',
          gap: '2px',
          p: '3px',
        }}
      >
        {opts.map(([k, label]) => (
          <Box
            key={k}
            component={'button'}
            onClick={() => view.setGroupBy(k)}
            title={label}
            sx={{
              alignItems: 'center',
              bgcolor: view.groupBy === k ? 'primary.main' : 'transparent',
              border: 'none',
              borderRadius: 999,
              color: view.groupBy === k ? 'common.white' : 'text.secondary',
              cursor: 'pointer',
              display: 'inline-flex',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 700,
              height: 32,
              px: 1.5,
              transition: 'all .14s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {label}
          </Box>
        ))}
      </Box>
    </Stack>
  );
};

const LevelSelect: React.FC<{
  view: UseVocabularyVerbsViewState;
  options: string[];
}> = ({ view, options }) => {
  const ref = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);
  const cur = view.levelFilter !== 'all' ? view.levelFilter : null;
  const curColor = cur ? getLevelColor(cur) : undefined;
  return (
    <Box sx={{ position: 'relative' }}>
      <Box
        component={'button'}
        ref={ref}
        onClick={() => setOpen((o) => !o)}
        sx={{
          alignItems: 'center',
          bgcolor: 'background.paper',
          border: '1px solid',
          borderColor: cur || open ? 'primary.main' : 'primary.tableBorder',
          borderRadius: 999,
          color: cur ? 'text.primary' : 'text.secondary',
          cursor: 'pointer',
          display: 'inline-flex',
          fontFamily: 'inherit',
          fontSize: 13.5,
          fontWeight: 700,
          gap: 0.875,
          height: 40,
          pl: 1.75,
          pr: 1.5,
          transition: 'border-color .14s ease',
          whiteSpace: 'nowrap',
        }}
      >
        {cur && curColor && (
          <Box
            sx={{
              bgcolor: hexToRgba(curColor, 0.18),
              borderRadius: 0.75,
              color: curColor,
              fontSize: 11.5,
              fontWeight: 800,
              px: 0.875,
              py: 0.125,
            }}
          >
            {cur}
          </Box>
        )}
        {cur ? 'Рівень' : 'Рівень: усі'}
        <ExpandMoreIcon
          sx={{
            color: 'text.secondary',
            fontSize: 18,
            transform: open ? 'rotate(180deg)' : 'none',
            transition: 'transform .16s ease',
          }}
        />
      </Box>
      <Popper
        open={open}
        anchorEl={ref.current}
        placement={'bottom-start'}
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'primary.tableBorder',
              borderRadius: 2,
              boxShadow: '0 24px 60px -20px rgba(20,20,20,0.45)',
              minWidth: 200,
              p: 0.75,
            }}
          >
            {[{ key: 'all', label: 'Усі рівні', color: '' }, ...options.map((l) => ({
              key: l,
              label: l,
              color: getLevelColor(l),
            }))].map((opt) => {
              const on = view.levelFilter === opt.key;
              return (
                <Box
                  key={opt.key}
                  component={'button'}
                  onClick={() => {
                    view.setLevelFilter(opt.key);
                    setOpen(false);
                  }}
                  sx={{
                    alignItems: 'center',
                    bgcolor: on
                      ? (t) =>
                          t.palette.mode === 'light'
                            ? 'rgba(59,44,147,0.10)'
                            : 'rgba(155,124,255,0.18)'
                      : 'transparent',
                    border: 'none',
                    borderRadius: 1.25,
                    color: on ? 'primary.main' : 'text.primary',
                    cursor: 'pointer',
                    display: 'flex',
                    fontFamily: 'inherit',
                    fontSize: 14,
                    fontWeight: on ? 700 : 500,
                    gap: 1.125,
                    px: 1.5,
                    py: 1.125,
                    textAlign: 'left',
                    transition: 'background-color .12s ease',
                    width: '100%',
                    '&:hover': { bgcolor: 'background.default' },
                  }}
                >
                  {opt.color && (
                    <Box
                      sx={{
                        bgcolor: hexToRgba(opt.color, 0.18),
                        borderRadius: 0.75,
                        color: opt.color,
                        fontSize: 11.5,
                        fontWeight: 800,
                        px: 0.875,
                        py: 0.125,
                      }}
                    >
                      {opt.label}
                    </Box>
                  )}
                  <Box sx={{ flex: 1 }}>{opt.color ? null : opt.label}</Box>
                  {on && <CheckIcon sx={{ color: 'primary.main', fontSize: 17 }} />}
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

const StatusChips: React.FC<{
  view: UseVocabularyVerbsViewState;
  scroll?: boolean;
}> = ({ view, scroll }) => {
  const theme = useTheme();
  // Single pill-shaped white container (mirrors GroupByControl). Each option
  // is a borderless segment that fills with primary.main when active. The
  // colored status dot stays inline so the chips read as "filter by lifecycle"
  // at a glance.
  return (
    <Box
      sx={{
        bgcolor: 'background.paper',
        border: '1px solid',
        borderColor: 'primary.tableBorder',
        borderRadius: 999,
        display: 'inline-flex',
        gap: '2px',
        maxWidth: scroll ? '100%' : undefined,
        overflowX: scroll ? 'auto' : 'visible',
        p: '3px',
        WebkitOverflowScrolling: scroll ? 'touch' : undefined,
      }}
    >
      {STATUS_FILTER_CHIPS.map((c) => {
        const on = view.statusFilter === c.value;
        const dotColor = c.status
          ? statusColor(c.status as WordStatus, theme.palette.mode)
          : undefined;
        return (
          <Box
            key={c.value}
            component={'button'}
            onClick={() => view.setStatusFilter(c.value as StatusFilterValue)}
            sx={{
              alignItems: 'center',
              bgcolor: on ? 'primary.main' : 'transparent',
              border: 'none',
              borderRadius: 999,
              color: on ? 'common.white' : 'text.secondary',
              cursor: 'pointer',
              display: 'inline-flex',
              flex: 'none',
              fontFamily: 'inherit',
              fontSize: 13,
              fontWeight: 700,
              gap: 0.75,
              height: 32,
              px: 1.5,
              transition: 'all .14s ease',
              whiteSpace: 'nowrap',
            }}
          >
            {dotColor && (
              <Box
                sx={{
                  bgcolor: on ? 'common.white' : dotColor,
                  borderRadius: '50%',
                  flex: 'none',
                  height: 8,
                  width: 8,
                }}
              />
            )}
            {c.label}
          </Box>
        );
      })}
    </Box>
  );
};

const StatusMarkButton: React.FC<{
  onMark: (status: WordStatus) => void;
}> = ({ onMark }) => {
  const theme = useTheme();
  const mode = theme.palette.mode;
  const ref = useRef<HTMLButtonElement | null>(null);
  const [open, setOpen] = useState(false);

  return (
    <Box sx={{ position: 'relative' }}>
      <Button
        ref={ref}
        size={'small'}
        variant={'outlined'}
        startIcon={<LabelOutlinedIcon />}
        endIcon={<ExpandMoreIcon sx={{ fontSize: 18 }} />}
        onClick={() => setOpen((o) => !o)}
        sx={{ height: 36, px: 1.5 }}
      >
        {'Позначити як'}
      </Button>
      <Popper
        open={open}
        anchorEl={ref.current}
        placement={'bottom-end'}
        modifiers={[{ name: 'offset', options: { offset: [0, 6] } }]}
        style={{ zIndex: 1300 }}
      >
        <ClickAwayListener onClickAway={() => setOpen(false)}>
          <Box
            sx={{
              bgcolor: 'background.paper',
              border: '1px solid',
              borderColor: 'primary.tableBorder',
              borderRadius: 2,
              boxShadow: '0 24px 60px -20px rgba(20,20,20,0.45)',
              p: 0.75,
              width: 248,
            }}
          >
            {STATUS_ORDER_BUTTONS.map((s) => {
              const meta = VOCAB_STATUSES[s];
              const c = statusColor(s, mode);
              return (
                <Box
                  key={s}
                  component={'button'}
                  onClick={() => {
                    onMark(s);
                    setOpen(false);
                  }}
                  sx={{
                    alignItems: 'center',
                    bgcolor: 'transparent',
                    border: 'none',
                    borderRadius: 1.25,
                    color: 'text.primary',
                    cursor: 'pointer',
                    display: 'flex',
                    fontFamily: 'inherit',
                    gap: 1.25,
                    px: 1.25,
                    py: 1,
                    textAlign: 'left',
                    transition: 'background-color .12s ease',
                    width: '100%',
                    '&:hover': { bgcolor: 'background.default' },
                  }}
                >
                  <Box
                    sx={{
                      bgcolor: c,
                      borderRadius: '50%',
                      flex: 'none',
                      height: 10,
                      width: 10,
                    }}
                  />
                  <Box sx={{ flex: 1, minWidth: 0 }}>
                    <Typography sx={{ fontSize: 14, fontWeight: 600 }}>
                      {meta.label}
                    </Typography>
                    <Typography
                      sx={{ color: 'text.secondary', fontSize: 12, lineHeight: 1.2 }}
                    >
                      {meta.desc}
                    </Typography>
                  </Box>
                </Box>
              );
            })}
          </Box>
        </ClickAwayListener>
      </Popper>
    </Box>
  );
};

const SelectionActions: React.FC<{
  view: UseVocabularyVerbsViewState;
  onBulkSetStatus: FilterBarProps['onBulkSetStatus'];
  onBulkDelete: FilterBarProps['onBulkDelete'];
}> = ({ view, onBulkSetStatus, onBulkDelete }) => {
  const editingActive = view.editingCount > 0;
  return (
    <>
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={0.875}
        sx={{ color: 'primary.main', fontSize: 14, fontWeight: 800 }}
      >
        <CheckCircleIcon sx={{ fontSize: 19 }} />
        <Box component={'span'}>
          {`${view.selectedCount} ${view.sliv(view.selectedCount)} обрано`}
        </Box>
      </Stack>
      <Box
        component={'button'}
        onClick={view.clear}
        sx={{
          alignItems: 'center',
          background: 'transparent',
          border: 'none',
          color: 'text.secondary',
          cursor: 'pointer',
          display: 'inline-flex',
          fontFamily: 'inherit',
          fontSize: 13.5,
          fontWeight: 600,
          gap: 0.5,
          p: 0,
          '&:hover': { color: 'text.primary' },
        }}
      >
        <CloseIcon sx={{ fontSize: 16 }} />
        {'Зняти вибір'}
      </Box>
      <Box sx={{ flex: 1 }} />
      {editingActive ? (
        <>
          <Typography sx={{ color: 'text.secondary', fontSize: 13 }}>
            {`Редагування ${view.editingCount} ${view.sliv(view.editingCount)}`}
          </Typography>
          <Button
            size={'small'}
            variant={'outlined'}
            startIcon={<CloseIcon />}
            onClick={view.cancelAllEdits}
            sx={{ height: 36, px: 1.5 }}
          >
            {'Скасувати'}
          </Button>
          <Button
            size={'small'}
            variant={'contained'}
            startIcon={<CheckIcon />}
            onClick={view.saveAllEdits}
            sx={{ height: 36, px: 1.5 }}
          >
            {'Зберегти всі'}
          </Button>
        </>
      ) : (
        <Stack direction={'row'} spacing={1} sx={{ flexWrap: 'wrap', rowGap: 1 }}>
          <Button
            size={'small'}
            variant={'outlined'}
            startIcon={<EditIcon />}
            onClick={view.startEditSelected}
            sx={{ height: 36, px: 1.5 }}
          >
            {'Редагувати'}
          </Button>
          <StatusMarkButton onMark={onBulkSetStatus} />
          <Button
            size={'small'}
            variant={'outlined'}
            color={'error'}
            startIcon={<DeleteOutlineIcon />}
            onClick={onBulkDelete}
            sx={{ height: 36, px: 1.5 }}
          >
            {'Видалити'}
          </Button>
        </Stack>
      )}
    </>
  );
};

const FilterBar: React.FC<FilterBarProps> = ({
  view,
  levelOptions,
  onBulkSetStatus,
  onBulkDelete,
  settingsAnchorRef,
  onToggleSettings,
  settingsOpen,
}) => {
  const theme = useTheme();
  const isPhone = useMediaQuery(theme.breakpoints.down('sm'));
  const selecting = view.selectedCount > 0;

  return (
    <Stack spacing={1.25} sx={{ mb: 2 }}>
      {/* Row 1: search + view-toggle + gear */}
      <Stack
        direction={'row'}
        alignItems={'center'}
        spacing={1.25}
        sx={{ flexWrap: 'wrap', rowGap: 1.25 }}
      >
        <SearchInput view={view} />
        <Box sx={{ flex: 1, display: { xs: 'none', md: 'block' } }} />
        {!isPhone && <ViewToggle view={view} />}
        <Tooltip
          title={
            view.viewMode === 'table'
              ? 'Налаштування таблиці'
              : 'Налаштування карток'
          }
          placement={'top'}
        >
          <IconButton
            ref={settingsAnchorRef}
            size={'small'}
            onClick={onToggleSettings}
            sx={(t) => ({
              bgcolor: settingsOpen
                ? t.palette.mode === 'light'
                  ? 'rgba(59,44,147,0.10)'
                  : 'rgba(155,124,255,0.18)'
                : 'background.paper',
              border: '1px solid',
              borderColor: settingsOpen ? 'primary.main' : 'primary.tableBorder',
              borderRadius: 1.25,
              color: settingsOpen ? 'primary.main' : 'text.secondary',
              height: 40,
              transition: 'all .14s ease',
              width: 40,
            })}
          >
            <TuneIcon sx={{ fontSize: 20 }} />
          </IconButton>
        </Tooltip>
      </Stack>

      {/*
        Row 2: filters or selection actions. Wrapped in a fixed-height Box so
        ticking a checkbox doesn't shift everything below (the selection bar
        adds a border + ~14px of vertical padding the filter row doesn't have).
      */}
      <Box
        sx={{
          alignItems: 'stretch',
          borderTop: '1px solid',
          borderColor: 'primary.tableBorder',
          display: 'flex',
          flexDirection: 'column',
          justifyContent: 'center',
          minHeight: { xs: 68, md: 60 },
          pt: 1.25,
        }}
      >
        {selecting ? (
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.25}
            sx={(t) => ({
              bgcolor:
                t.palette.mode === 'light'
                  ? 'rgba(59,44,147,0.05)'
                  : 'rgba(155,124,255,0.10)',
              border: '1px solid',
              borderColor: 'primary.main',
              borderRadius: 2,
              flexWrap: 'wrap',
              minHeight: 44,
              px: { xs: 1.25, md: 1.75 },
              py: { xs: 1, md: 0.875 },
              rowGap: 1,
            })}
          >
            <SelectionActions
              view={view}
              onBulkSetStatus={onBulkSetStatus}
              onBulkDelete={onBulkDelete}
            />
          </Stack>
        ) : isPhone ? (
          <Stack spacing={1.25}>
            <StatusChips view={view} scroll />
            <Stack
              direction={'row'}
              alignItems={'center'}
              justifyContent={'space-between'}
              spacing={1}
            >
              <LevelSelect view={view} options={levelOptions} />
              <GroupByControl view={view} compact />
            </Stack>
          </Stack>
        ) : (
          <Stack
            direction={'row'}
            alignItems={'center'}
            spacing={1.5}
            sx={{ flexWrap: 'wrap', minHeight: 44, rowGap: 1 }}
          >
            <StatusChips view={view} />
            <LevelSelect view={view} options={levelOptions} />
            <Box sx={{ flex: 1 }} />
            <GroupByControl view={view} />
          </Stack>
        )}
      </Box>
    </Stack>
  );
};

export default FilterBar;
